import {
  Box,
  Button,
  Flex,
  FormControl,
  NumberInput,
  NumberInputField,
  Text,
} from '@chakra-ui/react';
import { offerApi, OfferGroupDetail } from '@texas/api/endpoints/offerApi';
import { userApi } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  useTexasDrawer,
  TexasDrawer,
} from '@texas/components/shared/drawer/TexasDrawer';
import { TexasDrawerContent } from '@texas/components/shared/drawer/TexasDrawerContent';
import { TexasDrawerHeader } from '@texas/components/shared/drawer/TexasDrawerHeader';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import {
  ScrollableContainer,
  Scrollable,
} from '@texas/components/shared/scrollable/Scrollable';
import { ModalState } from '@texas/hooks/useChakraOutsideClick';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  offerGroupId: number;
  group: OfferGroupDetail;
  onUpdated: () => void;
}

export function EditOfferGroupDrawer({
  offerGroupId,
  group,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const drawer = useTexasDrawer(ModalState.Closed);

  const {
    data: users,
    refetch: refetchUsers,
    loading: usersLoading,
    error: usersError,
  } = useApiResource(userApi.getUsers);

  const {
    request: updateOwnerRequest,
    loading: updatingOwner,
    error: updateOwnerError,
  } = useApiRequest(offerApi.updateGroup);

  const {
    request: addTierRequest,
    loading: addingTier,
    error: addTierError,
  } = useApiRequest(offerApi.addTier);

  const { request: archiveTierRequest, loading: archivingTier } = useApiRequest(
    offerApi.archiveTier,
  );

  const { request: restoreTierRequest, loading: restoringTier } = useApiRequest(
    offerApi.restoreTier,
  );

  const [newQuantity, setNewQuantity] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  const ownerOptions = users?.map((u) => ({
    label: `${u.name}${u.email ? ` (${u.email})` : ''}`,
    value: u.id,
  }));
  const currentOwnerOption =
    ownerOptions?.find((o) => o.value === group.ownerId) ?? null;

  const handleOwnerChange = async (ownerId: number) => {
    await updateOwnerRequest(offerGroupId, { ownerId });
    onUpdated();
  };

  const handleAddTier = async () => {
    const qty = parseInt(newQuantity, 10);
    if (isNaN(qty) || qty <= 0) return;
    setAddError(null);
    const result = await addTierRequest(offerGroupId, { quantity: qty });
    if (!result.hasError) {
      setNewQuantity('');
      onUpdated();
    } else {
      setAddError(result.error.message);
    }
  };

  const handleArchive = async (tierId: number) => {
    await archiveTierRequest(offerGroupId, tierId);
    onUpdated();
  };

  const handleRestore = async (tierId: number) => {
    await restoreTierRequest(offerGroupId, tierId);
    onUpdated();
  };

  return (
    <Box>
      <TexasDrawer ref={drawer.ref} modal={drawer}>
        <TexasDrawerContent>
          <ScrollableContainer>
            <TexasDrawerHeader title={t('general.edit')} />
            <Scrollable>
              <Box p={4}>
                <FormControl mb={6}>
                  <TexasFormLabel>{t('customerUser.select')}</TexasFormLabel>
                  {usersError && <ErrorDetails error={usersError} />}
                  {updateOwnerError && (
                    <ErrorDetails error={updateOwnerError} />
                  )}
                  <TexasSelect
                    isLoading={usersLoading || updatingOwner}
                    value={currentOwnerOption}
                    onChange={(opt) => {
                      if (opt && 'value' in opt) {
                        handleOwnerChange((opt as { value: number }).value);
                      }
                    }}
                    options={ownerOptions}
                  />
                </FormControl>

                <Box>
                  <Text fontWeight="semibold" mb={2} fontSize="sm">
                    {t('offer.tiers')}
                  </Text>
                  <Flex direction="column" gap={2} mb={4}>
                    {group.tiers.map((tier) => (
                      <Flex
                        key={tier.id}
                        align="center"
                        justify="space-between"
                        gap={2}
                      >
                        <Text
                          fontSize="sm"
                          color={tier.archived ? 'gray.500' : undefined}
                          textDecoration={
                            tier.archived ? 'line-through' : undefined
                          }
                        >
                          {tier.quantity}
                        </Text>
                        {tier.archived ? (
                          <Button
                            size="xs"
                            variant="texas-light"
                            isLoading={restoringTier}
                            onClick={() => handleRestore(tier.id)}
                          >
                            {t('general.restore')}
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            variant="texas-light"
                            isLoading={archivingTier}
                            onClick={() => handleArchive(tier.id)}
                          >
                            {t('general.archive')}
                          </Button>
                        )}
                      </Flex>
                    ))}
                  </Flex>

                  <Flex gap={2} align="flex-start">
                    <FormControl flex={1}>
                      <NumberInput
                        size="sm"
                        min={1}
                        value={newQuantity}
                        onChange={(val: string) => {
                          setNewQuantity(val);
                          setAddError(null);
                        }}
                      >
                        <NumberInputField placeholder={t('inquiry.quantity')} />
                      </NumberInput>
                      {addError && (
                        <Text color="red.400" fontSize="xs" mt={1}>
                          {addError}
                        </Text>
                      )}
                      {addTierError && <ErrorDetails error={addTierError} />}
                    </FormControl>
                    <Button
                      size="sm"
                      variant="texas-solid"
                      isLoading={addingTier}
                      isDisabled={!newQuantity || addingTier}
                      onClick={handleAddTier}
                    >
                      {t('general.add')}
                    </Button>
                  </Flex>
                </Box>
              </Box>
            </Scrollable>
          </ScrollableContainer>
        </TexasDrawerContent>
      </TexasDrawer>
      <Button
        variant="texas-light"
        size="sm"
        onClick={() => drawer.setModalState(ModalState.Open)}
      >
        {t('general.edit')}
      </Button>
    </Box>
  );
}
